import { IAirlineWebapiService } from "./airline-webapi.service.interface";
import { HttpVerb } from "../../types/http-verb";
import { buildFullUrl, isHttpErrorStatusCode } from "../http/http-helpers";
import { IGetConfigurationsResponse } from "./responses/get-configurations.response";
import { ServiceBase } from "../service-base";
import { ILoginRequest } from "./requests/login.request";
import {ILoginResponse} from "./responses/login.response";
import { ICreateAuthorizedDotRezTokenRequest } from "./requests/create-authorized-dot-rez-token.request";
import { ICreateDotRezTokenResponse } from "./responses/create-dot-rez-token.response";
import { IRegisterUserRequest } from "./requests/register-user.request";
import { AirlineWebapiResponse } from "./airline-webapi-response";
import { AirlineWebapiErrorCodesEnum } from "./airline-webapi-error-codes.enum";
import { IDotRezCompanion } from "../dot-rez-api/data-contracts/user/companion-data-contracts";
import { IAddCompanionRequest } from "./requests/add-companion.request";
import { IUpdateCompanionRequest } from "./requests/update-companion.request";
import { IAddCompanionTravelDocumentRequest } from "./requests/add-companion-travel-document.request";
import { IDotRezPersonTravelDocument } from "../dot-rez-api/data-contracts/user/person.data-contracts";
import { IUpdateCompanionTravelDocumentRequest } from "./requests/update-companion-travel-document.request";
import { IDeleteCompanionTravelDocumentRequest } from "./requests/delete-companion-travel-document.request";
import { IDeleteCompanionRequest } from "./requests/delete-companion.request";
import { IValidateInvoiceRequest } from "./requests/validate-invoice.request";
import { IValidateInvoiceResponse } from "./responses/validate-invoice.response";
import { IGetBookingsByCustomerNumberResponse } from "./responses/get-past-bookings.response";
import { IValidateBlueBenefitsSubscriptionsRequest } from "./requests/validate-blue-benefits-subscriptions.request";
import { IValidateBlueBenefitsSubscriptionsResponse } from "./responses/validate-blue-benefits-subscriptions.response";
import {IMaintenanceConfiguration} from "../configuration/objects/maintenance-configuration.interface";
import {ISegmentsDelayResponse} from "./responses/segments-delay.response";
import {DeeplinkAgentTokenRequest} from "./requests/deeplink-agent-token.request";
import {DeeplinkAgentTokenResponse} from "./responses/deeplink-agent-token.response";
import {IImpersonateUserRequest} from "./requests/impersonate-user.request";
import {ICreateDotRezAnonymousTokenResponse} from "./responses/create-dotrez-anonymous-token.response";
import {IClientPaymentCallbacks} from "./requests/begin-payment-base.request";
import {ICreateAppleBoardingPassesRequest} from "./requests/create-apple-boarding-passes.request";
import {ICreateAppleBoardingPassesResponse} from "./responses/create-apple-boarding-passes.response";
import {
    ICreateAppleBoardingPassesBundleResponse
} from "./responses/create-apple-boarding-pass-bundle.response";
import {ICreateAppleBoardingPassesBundleRequest} from "./requests/create-apple-boarding-passes-bundle.request";
import {IUpdateBoardingPassesInWalletsRequest} from "./requests/update-boarding-passes-in-wallets.request";
import {IBookingPaymentMethodsRequest} from "./requests/booking-payment-methods.request";
import {IBookingPaymentMethodsResponse} from "./responses/booking-payment-methods.response";
import {IBeginBookingPaymentRequest} from "./requests/begin-booking-payment.request";
import {IBeginBookingPaymentResponse} from "./responses/begin-booking-payment.response";
import {IBookingPaymentStatusRequest} from "./requests/booking-payment-status.request";
import {IBookingPaymentStatusResponse} from "./responses/booking-payment-status.response";
import {IApplyPsoDiscountFeeRequest} from "./requests/apply-pso-discount-fee.request";
import {ICreateBookingFeeRequest} from "./requests/booking-fee-create.request";
import {NullableString} from "../../types/nullable-types";
import {IOverrideBookingFeeRequest} from "./requests/booking-fee-override.request";
import {IDeleteBookingFeeRequest} from "./requests/booking-fee-delete.request";
import {IMoveFlightRequest} from "./requests/move-flight.request";
import {IMoveFlightSeatsRequest} from "./requests/move-flight-seats.request";
import {IMoveFlightSeatsResponse} from "./responses/move-flight-seats.response";
import {IAddSeatRequest} from "./requests/add-seat.request";
import {IAddSeatResponse} from "./responses/add-seat.response";
import {IFinalizePspOrderRequest} from "./requests/finalize-psp-order.request";
import {IFinalizePspOrderResponse} from "./responses/finalize-psp-order.response";
import {ICancelPspOrderRequest} from "./requests/cancel-psp-order.request";
import { ICreateGoogleBoardingPassesRequest } from "./requests/create-google-boarding-passes.request";
import { ICreateGoogleBoardingPassesResponse } from "./responses/create-google-boarding-passes.response";
import {IApplyPsoDiscountFeeResponse} from "./responses/apply-pso-discount-fee.response";
import {IApplePayValidateMerchantRequest} from "./requests/apple-pay-validate-merchant.request";
import {IApplePayValidateMerchantResponse} from "./responses/apple-pay-validate-merchant.response";
import {IResetPasswordRequest} from "./requests/reset-password.request";
import {IBookingSearchResponse} from "./responses/booking-search.response";
import {
    IBookingSearchByBookingDateRequest, IBookingSearchByContactRequest,
    IBookingSearchByEmailRequest, IBookingSearchByLastNameRequest, IBookingSearchByPhoneNumberRequest,
    IBookingSearchByRecordLocatorRequest
} from "./requests/booking-search.requests";
import {IChangeExpiredPasswordRequest} from "./requests/chanage-expired-password.request";
import {IChangePasswordRequest} from "./requests/change-password.request";
import {IRecentBookingsRequest} from "./requests/recent-bookings.request";
import {IImpersonateToAnonymousRequest} from "./requests/impersonate-to-anonymouse.request";
import {
    IDownloadPsoFamilyDeclarationRequest,
    IDownloadPsoWorkerDeclarationRequest
} from "./requests/download-pso-declaration.request";
import {IDownloadBlobResponse} from "./responses/download-blob.response";
import {getCleanAppVersion} from "../../version";
import {Check} from "../../types/type-checking";
import {IClaimInsuranceRequest} from "./requests/claim-insurance.request";
import {IBookingMultipleFeesOverrideRequest} from "./requests/booking-multiple-fees-override.request";
import {ImpersonateToAnonymousResponse} from "./responses/impersonate-to-anonymous.response";
import {ImpersonateUserResponse} from "./responses/impersonate-user.response";
import {IInsuranceRefundDetailsRequest} from "./requests/insurance-refund-details.request";
import {IInsuranceRefundDetailsResponse} from "./responses/insurance-refund-details.response";


interface IFetchOptions {
    pathName: string;
    body?: any;
    doNotThrowOnError?: boolean;
}

export class AirlineWebapiService extends ServiceBase implements IAirlineWebapiService {


    private buildAirlineWebApiUrl(path: string): string {
        return buildFullUrl(this.services.configuration.airlineWebapiUrl, path);
    }

    private _getHeaderValueFromResponse(response: Response, headerName: string): NullableString {

        let value= response.headers.get(headerName);
        if(!value) {
            value = response.headers.get(headerName.toLowerCase());
        }

        return value;

    }

    private async _fetchBlob(method: HttpVerb, options: IFetchOptions): Promise<AirlineWebapiResponse<IDownloadBlobResponse>> {
        const url = this.buildAirlineWebApiUrl(options.pathName);
        const fetchResponse = await fetch(url, {
            method: method,
            body: options.body && JSON.stringify(options.body),
            headers: {
                'Content-Type': 'application/json',
                ...this.services.configuration.envConfigs.ngrokHeadersForWebApi
            }
        });

        if(!fetchResponse.ok) {
            return new AirlineWebapiResponse<IDownloadBlobResponse>(AirlineWebapiErrorCodesEnum.GeneralError, undefined);
        }


        try {
            const blob = await fetchResponse.blob();

            const fileName = this._getFileNameFromContentDisposition(fetchResponse);

            return new AirlineWebapiResponse<IDownloadBlobResponse>(AirlineWebapiErrorCodesEnum.Success, {
                blob: blob,
                fileName: fileName
            });
        } catch (err) {
            this.services.logger.error(`Failed to download blob from ${url}`, err);
            return new AirlineWebapiResponse<IDownloadBlobResponse>(AirlineWebapiErrorCodesEnum.GeneralError, undefined);
        }



    }

    private _getFileNameFromContentDisposition (fetchResponse: Response): NullableString {
        const contentDisposition = this._getHeaderValueFromResponse(fetchResponse, 'Content-Disposition');
        if (!contentDisposition) {
            return null;
        }
        const match = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
        return match ? match[2] : null;
    };

    private async _fetchData<TData = any>(method: HttpVerb, options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {

        const url = this.buildAirlineWebApiUrl(options.pathName);
        const fetchResponse = await fetch(url, {
            method: method,
            body: options.body && JSON.stringify(options.body),
            headers: {
                'Content-Type': 'application/json',
                ...this.services.configuration.envConfigs.ngrokHeadersForWebApi
            }
        });

        let fetchResponseData: any;
        try {
            fetchResponseData = await fetchResponse.json();
        } catch (err) {
            if(options.doNotThrowOnError) {
                this.services.logger.error(`Failed to parse response from airline webapi ${url}`, err);
                return new AirlineWebapiResponse<TData>(AirlineWebapiErrorCodesEnum.GeneralError);
            } else {
                throw err;
            }
        }

        if (isHttpErrorStatusCode(fetchResponse.status) || fetchResponseData.errorCode !== AirlineWebapiErrorCodesEnum.Success) {
            if (options.doNotThrowOnError) {
                return new AirlineWebapiResponse<TData>(fetchResponseData.errorCode || AirlineWebapiErrorCodesEnum.GeneralError, fetchResponseData.data);
            } else {
                throw new Error(`Failed to fetch from ${url}`);
            }
        }

        return new AirlineWebapiResponse<TData>(fetchResponseData.errorCode, fetchResponseData.data);
    }


    private async get<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('GET', options);
    }

    private async post<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('POST', options);
    }

    private async patch<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('PATCH', options);
    }

    private async put<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('PUT', options);
    }

    private async delete<TData = any>(options: IFetchOptions): Promise<AirlineWebapiResponse<TData>> {
        return await this._fetchData('DELETE', options);
    }

    async getConfigurations(language: string): Promise<IGetConfigurationsResponse> {
        let pathName = '/api/ClientConfigurations/v1/mobileApp';
        let requestBody: any = {
            language: language,
            clientVersion: getCleanAppVersion(),
            deviceType: this.services.device.deviceType
        }
        if(this.services.layout.shouldUseWebappLayout) {
            pathName = '/api/ClientConfigurations/v3/website';
            requestBody = {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(requestBody))
            }
        }

        const response = await this.post<IGetConfigurationsResponse>({
            pathName: pathName,
            body: requestBody
        });

        return response.data;
    }

    private async _decryptLoginDotRezToken(loginResponse: AirlineWebapiResponse<ILoginResponse>): Promise<AirlineWebapiResponse<ILoginResponse>> {
        if(loginResponse.hasError) {
            return loginResponse;
        }
        loginResponse.data.dotRezToken = await this.services.crypto.decryptServerMessage(loginResponse.data.dotRezToken);
        return loginResponse;
    }

    async createAnonymousDotRezToken(): Promise<ICreateDotRezAnonymousTokenResponse> {
        let pathName = '/api/user/v1/mobileapp/anonymousToken';
        if(this.services.layout.shouldUseWebappLayout) {
            pathName = '/api/user/v1/website/anonymousToken'
        }
        const response = await this.post<ICreateDotRezAnonymousTokenResponse>({
            pathName: pathName,
            body:{}
        });

        response.data.token = await this.services.crypto.decryptServerMessage(response.data.token);
        response.data.dotRezTokenExpirationDate = this.services.time.parseIsoDate(response.data.dotRezTokenExpirationDate as any);

        return response.data;
    }

    async createDogToken(): Promise<ICreateDotRezAnonymousTokenResponse> {
        const response = await this.post<ICreateDotRezAnonymousTokenResponse>({
            pathName: '/api/user/v1/dogToken',
            body:{}
        });

        response.data.token = await this.services.crypto.decryptServerMessage(response.data.token);
        response.data.dotRezTokenExpirationDate = this.services.time.parseIsoDate(response.data.dotRezTokenExpirationDate as any);

        return response.data
    }

    async getMaintenance(): Promise<IMaintenanceConfiguration> {
        const response = await this.get<IMaintenanceConfiguration>({
            pathName: '/api/ClientConfigurations/maintenance'
        });
        return response.data;
    }


    private async _createClientPaymentCallbacks(callbacks: IClientPaymentCallbacks): Promise<string> {

        const callbacksObj = {
            origin: window.location.origin,
            ...callbacks
        }

        return await this.services.crypto.encryptClientMessage(JSON.stringify(callbacksObj));
    }

    async validateBlueAirInvoice(request: IValidateInvoiceRequest): Promise<IValidateInvoiceResponse> {
        const response = await this.post<IValidateInvoiceResponse>({
            pathName: '/api/Invoice/v1/validateInvoice',
            body: {
                invoiceValidator: request.invoiceValidator
            },
            doNotThrowOnError: true
        });
        return response.data;
    }

    async login(request: ILoginRequest): Promise<AirlineWebapiResponse<ILoginResponse>> {
        const response = await this.post<ILoginResponse>({
            pathName: `/api/User/v1/login`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            },
            doNotThrowOnError: true
        });

        return await this._decryptLoginDotRezToken(response);

    }


    async createAuthorizedDotRezToken(request: ICreateAuthorizedDotRezTokenRequest): Promise<AirlineWebapiResponse<ICreateDotRezTokenResponse>> {
        const response = await this.post<ICreateDotRezTokenResponse>({
            pathName: '/api/User/v1/authorizedToken',
            body: request,
            doNotThrowOnError: true
        });

        if(response.hasError || !response.data?.dotRezToken) {
            return response;
        }

        response.data.dotRezToken = await this.services.crypto.decryptServerMessage(response.data.dotRezToken);
        response.data.dotRezTokenExpirationDate = this.services.time.parseIsoDate(response.data.dotRezTokenExpirationDate as any);

        return response;
    }

    async registerUser(request: IRegisterUserRequest): Promise<AirlineWebapiResponse<{}>> {
        return await this.post<{}>({
            pathName: '/api/User/v1/register',
            body: request,
            doNotThrowOnError: true
        });
    }

    async confirmRegistration(customerNumber: string): Promise<void> {
        await this.post({
            pathName: '/api/User/v1/confirmRegistration',
            body: {
                customerNumber: customerNumber
            }
        });
    }

    async sendResetPasswordLink(emailAddress: string): Promise<AirlineWebapiResponse<{}>> {
        return await this.post<{}>({
            pathName: '/api/PasswordRecovery/v1/sendPasswordResetLink',
            body: {
                emailAddress: emailAddress
            },
            doNotThrowOnError: true
        });
    }

    async resetPassword(request: IResetPasswordRequest): Promise<AirlineWebapiResponse<ILoginResponse>> {
        const response = await this.post<ILoginResponse>({
            pathName: '/api/PasswordRecovery/v1/resetPassword',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            },
            doNotThrowOnError: true
        });

        return await this._decryptLoginDotRezToken(response);
    }

    async changeExpiredPassword(request: IChangeExpiredPasswordRequest): Promise<ILoginResponse> {
        const response = await this.post<ILoginResponse>({
            pathName: `/api/User/v1/password/changeExpired`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        });

        return (await this._decryptLoginDotRezToken(response)).data;
    }

    async changePassword(request: IChangePasswordRequest): Promise<AirlineWebapiResponse<ILoginResponse>> {
        const response = await this.post<ILoginResponse>({
            pathName: '/api/User/v1/password/change',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            },
            doNotThrowOnError: true
        });

        return await this._decryptLoginDotRezToken(response);
    }

    async getCompanions(dotRezToken: string): Promise<IDotRezCompanion[]> {
        const response = await this.get<IDotRezCompanion[]>({
            pathName: `/api/Companions/v1/getCompanions?dotRezToken=${dotRezToken}`
        });

        return response.data;
    }

    async addCompanion(request: IAddCompanionRequest): Promise<IDotRezCompanion> {
        const response = await this.post<IDotRezCompanion>({
            pathName: `/api/Companions/v1/addCompanion`,
            body: request
        });

        return response.data;
    }

    async updateCompanion(request: IUpdateCompanionRequest): Promise<IDotRezCompanion> {
        const response = await this.patch({
            pathName: `/api/Companions/v1/updateCompanion`,
            body: request
        });
        return response.data;
    }

    async deleteCompanion(request: IDeleteCompanionRequest): Promise<void> {
        await this.delete({
            pathName: `/api/Companions/v1/deleteCompanion`,
            body: request
        });
    }

    async addCompanionTravelDocument(request: IAddCompanionTravelDocumentRequest): Promise<IDotRezPersonTravelDocument> {
        const response = await this.post<IDotRezPersonTravelDocument>({
            pathName: `/api/Companions/v1/addTravelDocument`,
            body: request
        });

        return response.data;
    }

    async updateCompanionTravelDocument(request: IUpdateCompanionTravelDocumentRequest): Promise<IDotRezPersonTravelDocument> {
        const response = await this.put<IDotRezPersonTravelDocument>({
            pathName: `/api/Companions/v1/updateTravelDocument`,
            body: request
        });

        return response.data;
    }

    async deleteCompanionTravelDocument(request: IDeleteCompanionTravelDocumentRequest): Promise<void> {
        await this.delete({
            pathName: `/api/Companions/v1/deleteTravelDocument`,
            body: request
        })
    }

    async getBookingsByCustomerNumber(dotRezToken: string): Promise<IGetBookingsByCustomerNumberResponse> {
        const response = await this.get<IGetBookingsByCustomerNumberResponse>({
            pathName: `/api/BookingHistory/v1/getBookingsByCustomerNumber?dotrezToken=${dotRezToken}`,
        });

        return response.data;
    }

    async getValidBlueBenefitsSubscriptions(request: IValidateBlueBenefitsSubscriptionsRequest): Promise<IValidateBlueBenefitsSubscriptionsResponse> {
        const response = await this.post<IValidateBlueBenefitsSubscriptionsResponse>({
            pathName: '/api/BlueBenefits/v1/validate',
            body: request
        });

        return response.data;
    }

    async getSegmentsDelays(recordLocator: string): Promise<ISegmentsDelayResponse> {
        const response = await this.get<ISegmentsDelayResponse>({
            pathName: `/api/BookingOperations/v1/segments/delays?RecordLocator=${recordLocator}`
        });

        return response.data;
    }


    async getDeepLinkAgentToken(request: DeeplinkAgentTokenRequest): Promise<DeeplinkAgentTokenResponse> {
        const response = await this.post<DeeplinkAgentTokenResponse>({
            pathName: '/api/User/v2/deepLinkAgentToken', body: {
                agentName: request.agentName
            }
        });

        response.data.dotRezToken = await this.services.crypto.decryptServerMessage(response.data.dotRezToken);
        response.data.dotRezTokenExpirationDate = this.services.time.parseIsoDate(response.data.dotRezTokenExpirationDate as any);

        return response.data;
    }

    async impersonateUser(request: IImpersonateUserRequest): Promise<ImpersonateUserResponse> {
        const apiResponse = await this.post({
            pathName: '/api/User/v1/impersonate', body: {
                agentName: request.agentName,
                dotRezToken: request.dotRezToken
            }
        });

        if(apiResponse.data.token) {
            return {
                token: await this.services.crypto.decryptServerMessage(apiResponse.data.token),
                dotRezTokenExpirationDate: this.services.time.parseIsoDate(apiResponse.data.dotRezTokenExpirationDate as any),
            }
        }

        return apiResponse.data;
    }

    async impersonateToAnonymous(request: IImpersonateToAnonymousRequest): Promise<ImpersonateToAnonymousResponse> {
        let pathName = '/api/user/v1/mobileapp/impersonateToAnonymous';
        if(this.services.layout.shouldUseWebappLayout) {
            pathName = '/api/user/v1/website/impersonateToAnonymous'
        }
        const apiResponse = await this.post<ImpersonateToAnonymousResponse>({
            pathName: pathName,
            body: request
        });

        if(apiResponse.data.token) {
            return {
                token: await this.services.crypto.decryptServerMessage(apiResponse.data.token),
                dotRezTokenExpirationDate: this.services.time.parseIsoDate(apiResponse.data.dotRezTokenExpirationDate as any),
            }
        }

        return apiResponse.data;

    }


    async createAppleBoardingPasses(request: ICreateAppleBoardingPassesRequest): Promise<ICreateAppleBoardingPassesResponse> {
        const response = await this.post<ICreateAppleBoardingPassesResponse>({
            pathName: '/api/boardingPass/apple/v1/create',
            body: request
        });

        return response.data;
    }

    async createGoogleBoardingPasses(request: ICreateGoogleBoardingPassesRequest): Promise<ICreateGoogleBoardingPassesResponse> {
        const response = await this.post<ICreateGoogleBoardingPassesResponse>({
            pathName: '/api/boardingPass/google/v1/create',
            body: request
        });
        return response.data;
    }

    async createAppleBoardingPassesBundle(request: ICreateAppleBoardingPassesBundleRequest): Promise<ICreateAppleBoardingPassesBundleResponse> {
        const response = await this.post<ICreateAppleBoardingPassesBundleResponse>({
            pathName: '/api/boardingPass/apple/v1/create/bundle',
            body: request
        });

        return response.data;
    }

    async updateBoardingPassesInWallets(request: IUpdateBoardingPassesInWalletsRequest): Promise<void> {
        await this.post({
            pathName: '/api/boardingPass/wallets/v1/pushUpdates',
            body: request
        });
    }


    async getBookingPaymentMethods(request: IBookingPaymentMethodsRequest): Promise<IBookingPaymentMethodsResponse> {
        let pathName = `/api/bookingPayment/v3/paymentMethods/website`;
        if(this.services.device.isHybrid) {
            pathName = `/api/bookingPayment/v3/paymentMethods/mobileApp`;
        }

        const requestBody = {
            dotRezToken: request.dotRezToken,
            clientVersion: getCleanAppVersion(),
            language: this.services.language.currentLanguage,
            insuranceQuotesRequest: request.insuranceQuotesRequest
        }



        const response = await this.post<IBookingPaymentMethodsResponse>({
            pathName: pathName,
            body: requestBody
        });

        return response.data;
    }

    async applePayValidateMerchant(request: IApplePayValidateMerchantRequest): Promise<IApplePayValidateMerchantResponse> {
        const host = new URL(window.location.href).host;

        const response = await this.post<IApplePayValidateMerchantResponse>({
            pathName: '/api/bookingPayment/v1/applePay/validateMerchant',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify({
                    ...request,
                    context: host
                }))
            }
        });

        return response.data;
    }

    async beginBookingPayment(request: IBeginBookingPaymentRequest): Promise<IBeginBookingPaymentResponse> {
        let pathName = `/api/bookingPayment/v2/website/begin`;
        if(this.services.device.isHybrid) {
            pathName = `/api/bookingPayment/v2/mobileApp/begin`;
        }

        const {callbacks, insuranceSubscriptionRequests, ...restOfTheRequestParams} = request;

        const response = await this.post<IBeginBookingPaymentResponse>({
            pathName: pathName,
            body: {
                ...restOfTheRequestParams,
                clientVersion: getCleanAppVersion(),
                channel: this.services.booking.getPaymentChannel(),
                clientOS: await this.services.device.getOperatingSystemName(),
                clientCallbacks: await this._createClientPaymentCallbacks(callbacks),
                insuranceSubscriptionRequests: Check.isEmpty(insuranceSubscriptionRequests) ? null : await this.services.crypto.encryptClientMessage(JSON.stringify(insuranceSubscriptionRequests))
            }
        });

        return response.data;
    }


    async finalizePspOrder(request: IFinalizePspOrderRequest): Promise<IFinalizePspOrderResponse> {
        const response = await this.post<IFinalizePspOrderResponse>({
            pathName: '/api/bookingPayment/v1/completePspOrder',
            body: request
        });

        return response.data;
    }

    async cancelPspOrder(request: ICancelPspOrderRequest): Promise<void> {
        await this.post({
            pathName: '/api/bookingPayment/v1/cancelPspOrder',
            body: request
        });
    }

    async getBookingPaymentStatus(request: IBookingPaymentStatusRequest): Promise<IBookingPaymentStatusResponse> {
        const response = await this.get<IBookingPaymentStatusResponse>({
            pathName: `/api/bookingPayment/v1/status/${request.paymentToken}`
        })

        return response.data;
    }

    async applyPsoDiscountFee(request: IApplyPsoDiscountFeeRequest): Promise<IApplyPsoDiscountFeeResponse> {
        let payload = JSON.stringify(request);

        payload = await this.services.crypto.encryptClientMessage(payload);

        const response = await this.post<IApplyPsoDiscountFeeResponse>({
            pathName: `/api/BookingOperations/v3/fees/marketDiscount`,
            body: {
                token: payload
            }
        });

        return response.data;
    }

    async createBookingFee(request: ICreateBookingFeeRequest): Promise<NullableString> {

        await this.post({
            pathName: `/api/BookingOperations/v1/fees`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        });

        return null;
    }
    async overrideBookingFee(request: IOverrideBookingFeeRequest): Promise<void> {
        await this.put({
            pathName: `/api/BookingOperations/v2/fees/${request.feeKey}`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify({
                    dotRezToken: request.dotRezToken,
                    amount: request.amount
                }))
            }
        });
    }


    async overrideBookingMultipleFees(request: IBookingMultipleFeesOverrideRequest): Promise<void> {
        await this.put({
            pathName: `/api/BookingOperations/v1/fees`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        });
    }

    async deleteBookingFee(request: IDeleteBookingFeeRequest): Promise<void> {
        await this.delete({
            pathName: `/api/BookingOperations/v2/fees/${request.feeKey}`,
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify({
                    dotRezToken: request.dotRezToken
                }))
            }
        });
    }

    async moveFlight(request: IMoveFlightRequest): Promise<void> {
        await this.post({
            pathName: '/api/BookingOperations/v1/moveFlight',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        });
    }

    async moveFlightSeats(request: IMoveFlightSeatsRequest): Promise<IMoveFlightSeatsResponse> {
        const response = await this.post<IMoveFlightSeatsResponse>({
            pathName: '/api/BookingOperations/v1/moveFlight/seats',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        });

        return response.data;
    }

    async addSeat(request: IAddSeatRequest): Promise<IAddSeatResponse> {
        const response = await this.post<IAddSeatResponse>({
            pathName: '/api/BookingOperations/v1/seats',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByRecordLocator(request: IBookingSearchByRecordLocatorRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byRecordLocator',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByEmail(request: IBookingSearchByEmailRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byEmail',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByBookingDate(request: IBookingSearchByBookingDateRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byBookingDate',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByContact(request: IBookingSearchByContactRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byBookingContact',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByLastName(request: IBookingSearchByLastNameRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byLastName',
            body: request
        });

        return response.data;
    }

    async agencyBookingSearchByPhoneNumber(request: IBookingSearchByPhoneNumberRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/byPhoneNumber',
            body: request
        });

        return response.data;
    }

    async agencyRecentBookings(request: IRecentBookingsRequest): Promise<IBookingSearchResponse> {
        const response = await this.post<IBookingSearchResponse>({
            pathName: '/api/AgencyBookingSearch/v1/recentBookings',
            body: request
        });

        return response.data;
    }


    async downloadPsoWorkersDeclaration(request: IDownloadPsoWorkerDeclarationRequest): Promise<AirlineWebapiResponse<IDownloadBlobResponse>> {
        return await this._fetchBlob('POST', {
            pathName: 'api/PsoDeclaration/workersPdf',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        })
    }

    async downloadPsoFamilyDeclaration(request: IDownloadPsoFamilyDeclarationRequest): Promise<AirlineWebapiResponse<IDownloadBlobResponse>> {
        return await this._fetchBlob('POST', {
            pathName: 'api/PsoDeclaration/familiesPdf',
            body: {
                token: await this.services.crypto.encryptClientMessage(JSON.stringify(request))
            }
        })
    }

    async getInsuranceRefundDetails(request: IInsuranceRefundDetailsRequest): Promise<IInsuranceRefundDetailsResponse> {
        const response = await this.post<IInsuranceRefundDetailsResponse>({
            pathName: '/api/Insurance/v1/refundDetails',
            body: request
        });

        return response.data;
    }

    async claimInsurance(request: IClaimInsuranceRequest): Promise<void> {
        await this.post<void>({
            pathName: '/api/Insurance/v1/claim',
            body: request
        });
    }
}
